import { useCallback, useMemo, useState } from "react";
import { ActionButton, Stack, IconButton } from "@fluentui/react";
import { Copy24Regular } from "@fluentui/react-icons";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import styles from "./Answer.module.css";
import { ChatAppResponse } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";

/** Normalize API message (string or unexpected shape) for copy / display checks */
function assistantMessagePlainText(content: unknown): string {
    if (typeof content === "string") {
        return content;
    }
    if (Array.isArray(content)) {
        return content
            .map((part: unknown) => {
                if (typeof part === "string") {
                    return part;
                }
                if (part && typeof part === "object" && "text" in part && typeof (part as { text: unknown }).text === "string") {
                    return (part as { text: string }).text;
                }
                return "";
            })
            .join("");
    }
    return "";
}

interface Props {
    answer: ChatAppResponse;
    isSelected?: boolean;
    isStreaming: boolean;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
}

export const ElisaBotAnswer = ({
    answer,
    isSelected,
    isStreaming,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions
}: Props) => {
    const [copyFeedback, setCopyFeedback] = useState<"idle" | "copied" | "error">("idle");

    const followupQuestions = answer.choices[0].context?.followup_questions;
    const messageContent = answer.choices[0].message.content;
    const plainTextForCopy = assistantMessagePlainText(messageContent);

    const copyAnswerToClipboard = useCallback(async () => {
        const text = plainTextForCopy;
        if (!text.trim() || isStreaming) {
            return;
        }
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const ta = document.createElement("textarea");
                ta.value = text;
                ta.setAttribute("readonly", "");
                ta.style.position = "fixed";
                ta.style.left = "-9999px";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            setCopyFeedback("copied");
            window.setTimeout(() => setCopyFeedback("idle"), 2000);
        } catch {
            setCopyFeedback("error");
            window.setTimeout(() => setCopyFeedback("idle"), 2000);
        }
    }, [plainTextForCopy, isStreaming]);
    // Parse without citation handler since we're not showing citations
    const parsedAnswer = useMemo(() => parseAnswerToHtml(plainTextForCopy, isStreaming, () => {}), [plainTextForCopy, isStreaming, answer]);
    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);

    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">
            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between">
                    <AnswerIcon />
                    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
                        <ActionButton
                            onRenderIcon={() => <Copy24Regular />}
                            title={
                                copyFeedback === "copied"
                                    ? "Copied!"
                                    : copyFeedback === "error"
                                    ? "Copy failed â€” try again"
                                    : "Copy assistant message (e.g. ticket text)"
                            }
                            ariaLabel="Copy assistant message to clipboard"
                            onClick={() => void copyAnswerToClipboard()}
                            disabled={isStreaming || !plainTextForCopy.trim()}
                            styles={{ root: { minHeight: 32, color: "black" } }}
                        >
                            Copy
                        </ActionButton>
                        <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: "Lightbulb" }}
                            title="Show thought process"
                            ariaLabel="Show thought process"
                            onClick={() => onThoughtProcessClicked()}
                            disabled={!answer.choices[0].context.thoughts?.length}
                        />
                        <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: "ClipboardList" }}
                            title="Show supporting content"
                            ariaLabel="Show supporting content"
                            onClick={() => onSupportingContentClicked()}
                            disabled={!answer.choices[0].context.data_points}
                        />
                    </div>
                </Stack>
            </Stack.Item>

            <Stack.Item grow>
                <div className={styles.answerText}>
                    <ReactMarkdown
                        children={sanitizedAnswerHtml}
                        rehypePlugins={[rehypeRaw]}
                        remarkPlugins={[remarkGfm]}
                        components={{
                            a: ({ node, children, ...props }) => (
                                <a {...props} target="_blank" rel="noopener noreferrer">
                                    {children}
                                </a>
                            )
                        }}
                    />
                </div>
            </Stack.Item>

            {/* SharePoint links section - only show if citations exist */}
            {!!parsedAnswer.citations.length && (
                <Stack.Item>
                    <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
                        <span className={styles.citationLearnMore}>Sharepoint:</span>
                        {parsedAnswer.citations.map((x, i) => {
                            const sharepointUris = answer.choices[0]?.context?.sharepoint_uris;
                            const entries = Object.entries(sharepointUris?.[x] || {});
                            const [key, value] = entries[0] || [];
                            const path = value;
                            return (
                                <a key={i} className={styles.citation} title={x} href={path} target="_blank" rel="noopener noreferrer">
                                    {`${i + 1}. ${key}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}

            {/* Follow-up questions */}
            {!!followupQuestions?.length && showFollowupQuestions && onFollowupQuestionClicked && (
                <Stack.Item>
                    <Stack horizontal wrap className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ""}`} tokens={{ childrenGap: 6 }}>
                        <span className={styles.followupQuestionLearnMore}>Follow-up questions:</span>
                        {followupQuestions.map((x, i) => {
                            return (
                                <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}
        </Stack>
    );
};
